import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ModificationFieldOverride, NewRowPrepareResult, RenderExtraContentFn } from '@qre-mailing/smart-table';
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, type Control, type Path, type UseFormSetValue, type UseFormWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { getAppByQreIdImportDatabases } from '../api/generated-evs';

import type { UsedRowType } from './useSamplesTableApi';

type ImportDbMap = Record<
  string,
  {
    name: string;
    enabled: boolean;
    prefillColumns?: Array<{ name: string; enabled: boolean; defaultValue?: string }>;
    additionalRequiredHeaders?: string[];
  }
>;

type DatabasePrefillContentProps = {
  watch: UseFormWatch<UsedRowType>;
  setValue: UseFormSetValue<UsedRowType>;
  control: Control<UsedRowType>;
  importMapRef: MutableRefObject<ImportDbMap>;
  formFieldNames: Set<string>;
};

function DatabasePrefillContent({
  watch,
  setValue,
  control,
  importMapRef,
  formFieldNames,
}: DatabasePrefillContentProps) {
  const selectedDb = watch('sampleDatabase' as Path<UsedRowType>) as string | undefined;
  const entry = selectedDb ? importMapRef.current[selectedDb] : undefined;
  const prefillColumns = entry?.prefillColumns;
  const additionalRequiredHeaders = entry?.additionalRequiredHeaders;
  const prevDbRef = useRef<string | undefined>(undefined);

  const extraPrefillColumns = useMemo(
    () => prefillColumns?.filter((col) => !formFieldNames.has(col.name)),
    [prefillColumns, formFieldNames]
  );

  useEffect(() => {
    if (selectedDb === prevDbRef.current) return;
    prevDbRef.current = selectedDb;

    if (!prefillColumns) return;
    for (const col of prefillColumns) {
      if (col.defaultValue !== undefined) {
        setValue(col.name as Path<UsedRowType>, col.defaultValue as never);
      }
    }
  }, [selectedDb, prefillColumns, setValue]);

  if (!entry || (!extraPrefillColumns?.length && !additionalRequiredHeaders?.length)) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      {additionalRequiredHeaders && additionalRequiredHeaders.length > 0 && (
        <Alert severity="warning" sx={{ mb: 0.5 }}>
          <FormattedMessage
            id="newRowDialog.additionalRequiredHeaders"
            defaultMessage="Pro tuto databázi jsou povinné také sloupce {headers}"
            values={{
              headers: <strong>{additionalRequiredHeaders.join(', ')}</strong>,
            }}
          />
        </Alert>
      )}
      {extraPrefillColumns && extraPrefillColumns.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 0.5 }}>
            <FormattedMessage id="newRowDialog.prefillColumns.title" defaultMessage="Prefill Columns" />
          </Typography>
          {extraPrefillColumns.map((col) => (
            <Controller
              key={col.name}
              name={col.name as Path<UsedRowType>}
              control={control}
              render={({ field: { ref, ...fieldData } }) => (
                <TextField
                  {...fieldData}
                  inputRef={ref}
                  size="small"
                  variant="outlined"
                  label={col.name}
                  fullWidth
                  value={fieldData.value ?? ''}
                  required={col.enabled && !col.defaultValue}
                  disabled={!col.enabled}
                />
              )}
            />
          ))}
        </>
      )}
    </Box>
  );
}

/**
 * New-row dialog: load import-databases (same as file import), default first enabled id,
 * Autocomplete editor for `sampleDatabase` fieldKey, and database-dependent prefill columns.
 */
export function useSampleNewRowConfig(qreId: string): {
  prepareNewRow: () => Promise<NewRowPrepareResult<UsedRowType>>;
  newRowFieldOverrides: Record<string, ModificationFieldOverride<UsedRowType>>;
  renderExtraContent: RenderExtraContentFn<UsedRowType>;
} {
  const intl = useIntl();
  const latestImportMapRef = useRef<ImportDbMap>({});

  const prepareNewRow = useCallback(async (): Promise<NewRowPrepareResult<UsedRowType>> => {
    const { data } = await getAppByQreIdImportDatabases({ path: { qreId }, throwOnError: true });
    const map = (data ?? {}) as ImportDbMap;
    latestImportMapRef.current = map;
    const keys = Object.keys(map).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const firstEnabled = keys.find((k) => map[k]?.enabled);
    return {
      defaultValues:
        firstEnabled != null ? ({ sampleDatabase: firstEnabled } as unknown as Partial<UsedRowType>) : undefined,
      fieldPrepareData: { sampleDatabase: map },
    };
  }, [qreId]);

  const newRowFieldOverrides = useMemo(
    (): Record<string, ModificationFieldOverride<UsedRowType>> => ({
      sampleDatabase: {
        rules: {
          required: intl.formatMessage({
            id: 'newRowDialog.database.required',
            defaultMessage: 'Vyberte databázi',
          }),
          validate: (v) => {
            const key = String(v ?? '');
            const invalid = intl.formatMessage({
              id: 'newRowDialog.database.invalid',
              defaultMessage: 'Vyberte platnou databázi ze seznamu',
            });
            if (!key.trim()) return invalid;
            const entry = latestImportMapRef.current[key];
            if (entry && entry.enabled) return true;
            return invalid;
          },
        },
        render: ({ column, field, fieldState, prepareData }) => {
          const map = (prepareData as ImportDbMap | undefined) ?? {};
          const loading = prepareData === undefined;
          const optionKeys = Object.keys(map).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
          const { ref, ...fieldData } = field;

          return (
            <Autocomplete
              loading={loading}
              options={optionKeys}
              value={(fieldData.value as string) || null}
              onChange={(_, newValue) => fieldData.onChange(newValue ?? '')}
              getOptionLabel={(option) => map[option]?.name ?? option}
              getOptionDisabled={(option) => !map[option]?.enabled}
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={ref}
                  size="small"
                  variant="outlined"
                  label={column.fieldName}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
            />
          );
        },
      },
    }),
    [intl]
  );

  const renderExtraContent: RenderExtraContentFn<UsedRowType> = useCallback(
    ({ watch, setValue, control, formFieldNames }) => (
      <DatabasePrefillContent
        watch={watch}
        setValue={setValue}
        control={control}
        importMapRef={latestImportMapRef}
        formFieldNames={formFieldNames}
      />
    ),
    []
  );

  return { prepareNewRow, newRowFieldOverrides, renderExtraContent };
}
